import { RefObject, useCallback, useEffect } from 'react';
import { isArray, isEmpty } from 'lodash';

const useOnClickOutside = (
    refs: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[] | null = null,
    callback: (event: MouseEvent | TouchEvent) => void,
    isSelfClickable = false,
    shouldListenInCapturePhase = false,
) => {
    const checkIfClickIsNotOutside = useCallback(
        (event) => {
            if (isEmpty(refs)) {
                return true;
            } else if (!isArray(refs)) {
                if (!refs.current || 'contains' in refs.current === false) {
                    return true;
                }
                return refs.current.contains(event.target);
            } else {
                return refs.filter((ref) => Boolean(ref?.current)).some((ref) => ref.current?.contains(event.target));
            }
        },
        [refs],
    );

    useEffect(
        () => {
            const listener = (event: MouseEvent | TouchEvent) => {
                // Do nothing if clicking ref's element or descendent elements
                if (checkIfClickIsNotOutside(event) || isSelfClickable) {
                    return;
                }
                callback(event);
            };
            document.addEventListener('mousedown', listener, shouldListenInCapturePhase);
            document.addEventListener('touchstart', listener, shouldListenInCapturePhase);
            return () => {
                document.removeEventListener('mousedown', listener, shouldListenInCapturePhase);
                document.removeEventListener('touchstart', listener, shouldListenInCapturePhase);
            };
        },
        // Add ref and callback to effect dependencies
        // It's worth noting that because passed in handler is a new ...
        // ... function on every render that will cause this effect ...
        // ... callback/cleanup to run every render. It's not a big deal ...
        // ... but to optimize you can wrap handler in useCallback before ...
        // ... passing it into this hook.
        [refs, callback, isSelfClickable, checkIfClickIsNotOutside, shouldListenInCapturePhase],
    );
};

export default useOnClickOutside;
